import { V3_SYNTH_MARKET_KEYS, V3SynthMarketKey } from '@kwenta/sdk/constants'
import { formatNumber } from '@kwenta/sdk/utils'
import { wei } from '@synthetixio/wei'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ValueType } from 'react-select'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import NumericInput from 'components/Input/NumericInput'
import { FlexDivRowCentered } from 'components/layout/flex'
import { V3_DEPOSITABLE_ASSETS } from 'constants/perpsV3'
import { selectShowModalWithParams } from 'state/app/selectors'
import { selectSynthV3Balances } from 'state/balances/selectors'
import {
	approveCrossMarginDeposit,
	depositCrossMarginMargin,
} from 'state/futures/crossMargin/actions'
import {
	selectDepositAllowances,
	selectMarginEnginePermitted,
} from 'state/futures/crossMargin/selectors'
import {
	selectIsApprovingCrossDeposit,
	selectIsSubmittingIsolatedTransfer,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import AssetSelector from './AssetSelector'
import MaxButton from './MaxButton'

const DEPOSIT_OPTIONS: { label: string; value: V3SynthMarketKey }[] = Object.keys(
	V3_DEPOSITABLE_ASSETS
).map((key) => ({
	label: V3_DEPOSITABLE_ASSETS[key as V3SynthMarketKey],
	value: key as V3SynthMarketKey,
}))

type Props = {
	closeOnComplete?: boolean
}

const DepositCrossMargin = memo(({ closeOnComplete }: Props) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const allowances = useAppSelector(selectDepositAllowances)
	const v3Balances = useAppSelector(selectSynthV3Balances)
	const submitting = useAppSelector(selectIsSubmittingIsolatedTransfer)
	const approving = useAppSelector(selectIsApprovingCrossDeposit)
	const marginEnginePermitted = useAppSelector(selectMarginEnginePermitted)
	const modalParams = useAppSelector(selectShowModalWithParams)?.params

	const [selectedAsset, setSelectedAsset] = useState<V3SynthMarketKey>('SNXUSD')
	const [amount, setAmount] = useState('')

	useEffect(() => {
		const asset = modalParams?.asset
		if (V3_SYNTH_MARKET_KEYS.includes(asset)) {
			setSelectedAsset(asset)
		}
	}, [modalParams?.asset])

	const minDeposit = useMemo(() => {
		// TODO: Update min deposit amount for cross margin
		return wei(0)
	}, [])

	const requiresApproval = useMemo(() => {
		const spender = marginEnginePermitted ? 'marginEngine' : 'marketProxy'
		return allowances[selectedAsset]?.[spender]?.lt(amount || 0)
	}, [allowances, amount, selectedAsset, marginEnginePermitted])

	const selectedAssetBalance = useMemo(
		() => v3Balances[selectedAsset] ?? wei(0),
		[selectedAsset, v3Balances]
	)

	const depositDisabled = useMemo(() => {
		if (!amount || submitting || approving) {
			return true
		}
		const amtWei = wei(amount)
		if (amtWei.eq(0) || amtWei.gt(selectedAssetBalance) || amtWei.lt(minDeposit)) {
			return true
		}
		return false
	}, [amount, selectedAssetBalance, minDeposit, submitting, approving])

	const value = useMemo(
		() => DEPOSIT_OPTIONS.find((option) => option.value === selectedAsset),
		[selectedAsset]
	)

	const onSelectAsset = useCallback((asset: ValueType<any>) => {
		setAmount('')
		setSelectedAsset(asset.value as V3SynthMarketKey)
	}, [])

	const handleSetMax = useCallback(() => {
		setAmount(selectedAssetBalance.toString())
	}, [selectedAssetBalance])

	const onDepositOrApprove = useCallback(() => {
		if (requiresApproval) {
			dispatch(approveCrossMarginDeposit(selectedAsset))
		} else {
			dispatch(
				depositCrossMarginMargin({ amount: wei(amount), synthKey: selectedAsset, closeOnComplete })
			)
		}
	}, [requiresApproval, selectedAsset, amount, closeOnComplete, dispatch])

	return (
		<>
			<AssetSelector selectedValue={value} onSelectAsset={onSelectAsset} />
			<BalanceContainer>
				<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
				<BalanceText>
					<span>{formatNumber(selectedAssetBalance, { suggestDecimals: true })}</span>{' '}
					{V3_DEPOSITABLE_ASSETS[selectedAsset]}
				</BalanceText>
			</BalanceContainer>
			<NumericInput
				dataTestId="futures-market-trade-deposit-margin-input"
				placeholder={'0.00'}
				value={amount}
				onChange={(_, v) => setAmount(v)}
				right={
					<MaxButton onClick={handleSetMax}>{t('futures.market.trade.margin.modal.max')}</MaxButton>
				}
			/>
			<MinimumAmountDisclaimer>
				{t('futures.market.trade.margin.modal.deposit.disclaimer')}
			</MinimumAmountDisclaimer>

			<TransactionButton
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={depositDisabled}
				loading={submitting || approving}
				fullWidth
				onClick={onDepositOrApprove}
				variant="flat"
			>
				{requiresApproval
					? t('futures.market.trade.margin.modal.deposit.approve-button', {
							asset: V3_DEPOSITABLE_ASSETS[selectedAsset],
					  })
					: t('futures.market.trade.margin.modal.deposit.button')}
			</TransactionButton>
		</>
	)
})

export default DepositCrossMargin

export const BalanceContainer = styled(FlexDivRowCentered)`
	margin-bottom: 8px;
	p {
		margin: 0;
	}
`

export const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const MinimumAmountDisclaimer = styled.div`
	font-size: 12px;
	margin: 14px 0;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	text-align: center;
`
